import React from 'react';
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import NumericField from '../NumericField';
import { PARTNER_POSITIONS, isNobodyRole } from '../../utils/partnerRoles';

const validatePartnerCommission = (percent) => {
  const num = Number(percent);
  return num >= 0 && num <= 20;
};

// Role assignment editor used by the proposal dialog and App Configuration › Default Roles.
// onNotify({ message, severity }) surfaces validation feedback through the host's snackbar.
const PartnerRolesEditor = ({ roles, entities, disabled = false, onChange, onNotify }) => {
  const notify = (message, severity) => {
    if (onNotify) onNotify({ message, severity });
  };

  const handleRoleChange = (index, field, value) => {
    if (disabled) return;
    const newRoles = roles.map((r) => ({ ...r }));
    if (field === 'percent') {
      const num = Number(value);
      if (!validatePartnerCommission(num)) {
        notify('Each role must be 0% to 20%', 'warning');
        return;
      }
      if (isNobodyRole(newRoles[index])) {
        newRoles[index].percent = 0;
        notify('When Nobody is selected, percent is forced to 0%', 'info');
      } else {
        newRoles[index].percent = num;
      }
    } else if (field === 'entityId') {
      if (value === 'nobody') {
        newRoles[index].entityId = null;
        newRoles[index].entityName = 'Nobody';
        newRoles[index].percent = 0;
        onChange(newRoles);
        return;
      }
      const role = newRoles[index];
      const selected = entities.find(e => e.id === value);
      // Enforce entity type: company role -> company entities; others -> partner entities
      if (selected) {
        if (role.positionId === 'company' && selected.entityType !== 'company') {
          notify('Company role must be assigned to a Company entity', 'warning');
          return;
        }
        if (role.positionId !== 'company' && selected.entityType !== 'partner') {
          notify('Non-company roles must be assigned to Partner entities', 'warning');
          return;
        }
      }
      newRoles[index].entityId = value || null;
      newRoles[index].entityName = selected ? selected.entityName : '';
    }
    onChange(newRoles);
  };

  return (
    <Grid container spacing={2}>
      {roles.map((role, index) => {
        const position = PARTNER_POSITIONS.find(p => p.id === role.positionId);
        const isCompanyRole = role.positionId === 'company';
        const isNobody = isNobodyRole(role);
        const entityOptions = isCompanyRole
          ? entities.filter(e => e.entityType === 'company' && e.accountType !== 'sub')
          : entities.filter(e => e.entityType === 'partner' && e.accountType !== 'sub');
        return (
          <Grid item xs={12} key={role.positionId}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" fontWeight="bold">{position?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{position?.description}</Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth>
                      <InputLabel>{isCompanyRole ? 'Assigned Company' : 'Assigned Partner'}</InputLabel>
                      <Select
                        value={role.entityId === null ? (role.entityName === 'Nobody' ? 'nobody' : '') : role.entityId}
                        label={isCompanyRole ? 'Assigned Company' : 'Assigned Partner'}
                        onChange={(e) => handleRoleChange(index, 'entityId', e.target.value)}
                        disabled={disabled}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {!isCompanyRole && (
                          <MenuItem value="nobody">Nobody</MenuItem>
                        )}
                        {entityOptions.map(entity => (
                          <MenuItem key={entity.id} value={entity.id}>{entity.entityName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NumericField
                      key={`percent-${role.positionId}-${role.entityId ?? (isNobody ? 'nobody' : 'assigned')}`}
                      fullWidth
                      label="Percentage"
                      value={isNobody ? 0 : role.percent}
                      onValueChange={(n) => handleRoleChange(index, 'percent', n ?? 0)}
                      mode="decimal"
                      decimals={1}
                      suffix="%"
                      min={0}
                      max={20}
                      disabled={disabled || isNobody}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PartnerRolesEditor;
